/**
 * offlineOutbox — IndexedDB-backed queue of pending mutations to be
 * replayed when the browser regains connectivity.
 *
 * Scope (MVP, 2026-02-04):
 *   POST/PATCH on /trips/{id}/packing, /journal, /places, /reservations
 *
 * Design notes
 * ------------
 * • Client-generated UUIDs — creates use `crypto.randomUUID()` so the same
 *   ID is used for the optimistic local record and the eventual server
 *   record. That eliminates the tempId → realId mapping bug class.
 * • Never delete failed entries silently — an entry stays in the store with
 *   `status: 'failed'` and `lastError` until the user dismisses it from the
 *   badge modal.
 * • Never lose ordering — flush processes entries by `createdAt` asc.
 * • Kill-switch — if `window.__tovias_offline_write_disabled === true`
 *   (set from the backend health header on load), the enqueue helper is a
 *   no-op and callers get the normal online-failure error path.
 */

const DB_NAME = 'tovias-offline';
const DB_VERSION = 1;
const STORE = 'outbox';

/** Open (and lazily create) the IndexedDB. */
function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('status', 'status');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const p = Promise.resolve(fn(store));
    tx.oncomplete = () => p.then(resolve, reject);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Enqueue a pending mutation.
 * @param {object} entry
 * @param {string} entry.method  'POST' | 'PATCH'
 * @param {string} entry.url     Full URL including /api prefix
 * @param {any}    entry.body    JSON-serialisable request body
 * @param {string} entry.tripId  For grouping/badge display
 * @param {string} entry.resource  'packing' | 'journal' | 'places' | 'reservations'
 * @param {string} [entry.action]  'create' | 'update' | 'toggle' (for UI copy)
 * @param {string} [entry.label]   Human-friendly summary shown in the badge
 * @returns {Promise<object>} the stored record
 */
export async function enqueue(entry) {
  if (typeof window !== 'undefined' && window.__tovias_offline_write_disabled) {
    throw new Error('offline write disabled');
  }
  const rec = {
    id: (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `off-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
    status: 'pending',
    ...entry,
  };
  await withStore('readwrite', (store) => {
    return new Promise((res, rej) => {
      const r = store.add(rec);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
  });
  _notify();
  return rec;
}

/** List all queued entries, oldest first. */
export async function list() {
  return withStore('readonly', (store) =>
    new Promise((res, rej) => {
      const rows = [];
      const cur = store.index('createdAt').openCursor();
      cur.onsuccess = (e) => {
        const c = e.target.result;
        if (c) {
          rows.push(c.value);
          c.continue();
        } else {
          res(rows);
        }
      };
      cur.onerror = () => rej(cur.error);
    }),
  );
}

/** Count pending (not failed) entries. */
export async function countPending() {
  const rows = await list();
  return rows.filter((r) => r.status !== 'failed').length;
}

/** Count failed entries. */
export async function countFailed() {
  const rows = await list();
  return rows.filter((r) => r.status === 'failed').length;
}

/** Total count regardless of status. */
export async function count() {
  return withStore('readonly', (store) =>
    new Promise((res, rej) => {
      const r = store.count();
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    }),
  );
}

/** Update a record's fields (attempts, status, lastError…). */
async function update(id, patch) {
  return withStore('readwrite', (store) =>
    new Promise((res, rej) => {
      const g = store.get(id);
      g.onsuccess = () => {
        const rec = g.result;
        if (!rec) { res(null); return; }
        const merged = { ...rec, ...patch };
        const p = store.put(merged);
        p.onsuccess = () => res(merged);
        p.onerror = () => rej(p.error);
      };
      g.onerror = () => rej(g.error);
    }),
  );
}

/** Remove one entry from the queue. */
export async function remove(id) {
  await withStore('readwrite', (store) =>
    new Promise((res, rej) => {
      const r = store.delete(id);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    }),
  );
  _notify();
}

/** Remove every FAILED entry (used by "Descartar todos" in the modal). */
export async function purgeFailed() {
  const rows = await list();
  for (const r of rows) if (r.status === 'failed') await remove(r.id);
}

const MAX_ATTEMPTS = 3;

/**
 * Attempt to send each pending entry to the server. Called on `online`
 * events, on app boot, and on an interval. Safe to call from multiple
 * places — a mutex prevents concurrent flushes.
 */
let _flushing = false;
export async function flush() {
  if (_flushing) return { skipped: true };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { skipped: true, reason: 'offline' };
  }
  _flushing = true;
  let ok = 0;
  let failed = 0;
  try {
    const rows = await list();
    for (const rec of rows) {
      if (rec.status === 'failed') continue;
      try {
        const res = await fetch(rec.url, {
          method: rec.method,
          headers: {
            'Content-Type': 'application/json',
            ...(rec.headers || {}),
          },
          credentials: 'include',
          body: rec.body !== undefined ? JSON.stringify(rec.body) : undefined,
        });
        if (res.ok) {
          await remove(rec.id);
          ok += 1;
        } else if (res.status === 404 || res.status === 409 || res.status === 422) {
          // Client-side error: never resolves by retrying. Move to failed.
          const text = await res.text().catch(() => '');
          await update(rec.id, {
            status: 'failed',
            lastError: `HTTP ${res.status}: ${text.slice(0, 200)}`,
            attempts: (rec.attempts || 0) + 1,
          });
          failed += 1;
        } else {
          // Transient (5xx, 401, 429). Increment attempts, mark failed if maxed.
          const attempts = (rec.attempts || 0) + 1;
          await update(rec.id, {
            attempts,
            status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
            lastError: `HTTP ${res.status}`,
          });
          if (attempts >= MAX_ATTEMPTS) failed += 1;
        }
      } catch (err) {
        // Network error: keep retryable until MAX_ATTEMPTS
        const attempts = (rec.attempts || 0) + 1;
        await update(rec.id, {
          attempts,
          status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
          lastError: String(err?.message || err).slice(0, 200),
        });
        if (attempts >= MAX_ATTEMPTS) failed += 1;
      }
    }
  } finally {
    _flushing = false;
    _notify();
  }
  return { ok, failed };
}

/** Mark a failed entry as pending again — used by the "Reintentar" button. */
export async function retry(id) {
  await update(id, { status: 'pending', attempts: 0, lastError: null });
  _notify();
  return flush();
}

// ─── Subscription helper for React components ──────────────────────────
const listeners = new Set();
function _notify() {
  for (const fn of listeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
